/**
 * „Gültig bis“ eines versendeten Angebots — Hilfen für den Ausweg aus „Angebot abgelaufen“
 * (Regel `.claude/rules/keine-sackgassen.md`). Dieselbe Rechnung wie der Server (`apps/api/src/lib/gueltigkeit.ts`):
 * Berliner Kalendertag, Vorgabe heute + 14 Tage, nie über den Veranstaltungstag hinaus.
 */

/** Heutiger Kalendertag in Berlin als „YYYY-MM-DD“. */
export function heuteBerlin(jetzt: Date = new Date()): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(jetzt);
}

export function plusTageIso(iso: string, tage: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

/** Ist ein „Gültig bis“ (YYYY-MM-DD) vor heute? Ohne Datum nie abgelaufen. */
export function gueltigkeitAbgelaufen(bis: string | null | undefined, heute: string = heuteBerlin()): boolean {
  return Boolean(bis) && (bis as string) < heute;
}

/** Vorgabe im Dialog: heute + 14 Tage, höchstens der Veranstaltungstag (liegt der schon hinter uns, bleibt es heute + 14). */
export function vorschlagGueltigBis(eventDate: string | null | undefined, heute: string = heuteBerlin()): string {
  const vorschlag = plusTageIso(heute, 14);
  return eventDate && eventDate >= heute && eventDate < vorschlag ? eventDate : vorschlag;
}

/** „2026-11-04“ -> „04.11.2026“ (ohne Zeitzonenfalle). */
export function datumDe(iso: string): string {
  const [j, m, t] = iso.slice(0, 10).split("-");
  return `${t}.${m}.${j}`;
}

/** Prüft ein gewähltes neues „Gültig bis“; liefert einen deutschen Satz oder `null`, wenn es passt. */
export function gueltigBisProblem(bis: string, eventDate: string | null | undefined, heute: string = heuteBerlin(), bisher?: string | null): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(bis)) return "Bitte ein Datum wählen.";
  if (bis < heute) return `Das Datum ${datumDe(bis)} liegt in der Vergangenheit. Bitte ein Datum ab heute wählen.`;
  if (eventDate && bis > eventDate) return `Die Gültigkeit darf nicht über den Veranstaltungstag (${datumDe(eventDate)}) hinausgehen.`;
  // Kein stilles Verkürzen (wortgleich zum Server): liegt das bisherige „Gültig bis“ noch in der Zukunft, darf das neue nicht früher sein.
  if (bisher && bisher >= heute && bis < bisher) return `Die Gültigkeit läuft schon bis ${datumDe(bisher)}. Ein früheres Datum würde sie verkürzen - bitte ein Datum ab ${datumDe(bisher)} wählen.`;
  return null;
}

/** Liegt der Veranstaltungstag vor heute? Dann gibt es kein gültiges „Gültig bis“ mehr (wortgleich zum Server). */
export function veranstaltungVergangen(eventDate: string | null | undefined, heute: string = heuteBerlin()): boolean {
  return Boolean(eventDate) && (eventDate as string).slice(0, 10) < heute;
}

export const VERANSTALTUNG_VERGANGEN_SATZ = "Die Veranstaltung liegt in der Vergangenheit. Termin ändern oder ein neues Angebot senden.";
